import {
  Badge, Button, Card, EmptyState, ErrorState, Field, Input, LoadingState, Notice, PageHeader, Row,
  Select, Stack, Textarea, useApi, useClampedPage, useI18n,
  type AnnouncementDeliveryResponse, type PlatformAnnouncementResponse, type TenantStatus,
} from '@daghfal/core';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

const TARGETABLE: TenantStatus[] = ['Trial', 'Active', 'PastDue', 'Suspended'];

/**
 * Platform-wide announcements (FR-PLT-07).
 *
 * Written first, sent deliberately. An announcement addressed to every tenant on the platform is not
 * something to let out as a side effect of submitting a form, so a draft is created, its reach can be
 * checked, and sending is a separate decision that cannot be taken twice.
 *
 * Owners and managers receive these; guardians and teaching staff never do. Archived tenants are excluded
 * by the server whatever the filter says — they are no longer customers.
 */
export function Announcements() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t, formatNumber } = useI18n();

  const [page, setPage] = useState(1);
  const [composing, setComposing] = useState(false);
  const [delivery, setDelivery] = useState<AnnouncementDeliveryResponse | null>(null);

  const announcements = useQuery({
    queryKey: ['platform', 'announcements', { page }],
    queryFn: () => api.platform.listAnnouncements({ page, pageSize: 20 }),
    placeholderData: keepPreviousData,
  });
  useClampedPage(page, setPage, announcements.data?.totalPages);

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['platform', 'announcements'] });
  };

  const send = useMutation({
    mutationFn: (id: number) => api.platform.sendAnnouncement(id),
    onSuccess: (result) => { setDelivery(result); refresh(); },
  });

  const discard = useMutation({
    mutationFn: (id: number) => api.platform.deleteAnnouncementDraft(id),
    onSuccess: refresh,
  });

  return (
    <>
      <PageHeader
        title={t('announcements.title')}
        lede={t('announcements.lede')}
        actions={
          <Button icon="＋" onClick={() => setComposing((open) => !open)}>
            {composing ? t('action.cancel') : t('announcements.compose')}
          </Button>
        }
      />

      {delivery && (
        <Notice tone="success" title={t('announcements.sent.title')}>
          {t('announcements.sent.summary', {
            tenants: formatNumber(delivery.tenantsTargeted),
            people: formatNumber(delivery.recipientsNotified),
          })}
        </Notice>
      )}

      {composing && (
        <ComposeForm
          onCreated={() => { refresh(); setComposing(false); setPage(1); }}
          onCancel={() => setComposing(false)}
        />
      )}

      {send.error != null && <ErrorState error={send.error} />}
      {discard.error != null && <ErrorState error={discard.error} />}

      {announcements.isPending && <LoadingState />}
      {announcements.error != null && (
        <ErrorState error={announcements.error} onRetry={() => void announcements.refetch()} />
      )}

      {announcements.data?.items.length === 0 && (
        <EmptyState icon="📣" title={t('announcements.empty')} body={t('announcements.empty.body')} />
      )}

      <Stack>
        {announcements.data?.items.map((announcement) => (
          <AnnouncementCard
            key={announcement.id}
            announcement={announcement}
            busy={send.isPending || discard.isPending}
            onSend={() => send.mutate(announcement.id)}
            onDiscard={() => discard.mutate(announcement.id)}
          />
        ))}
      </Stack>

      {announcements.data && announcements.data.totalPages > 1 && (
        <Row between>
          <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((n) => n - 1)}>
            {t('action.previous')}
          </Button>
          <span>{formatNumber(page)} / {formatNumber(announcements.data.totalPages)}</span>
          <Button
            variant="secondary"
            disabled={page >= announcements.data.totalPages}
            onClick={() => setPage((n) => n + 1)}
          >
            {t('action.next')}
          </Button>
        </Row>
      )}
    </>
  );
}

function AnnouncementCard({ announcement, busy, onSend, onDiscard }: {
  announcement: PlatformAnnouncementResponse;
  busy: boolean;
  onSend: () => void;
  onDiscard: () => void;
}) {
  const api = useApi();
  const { t, formatDateTime, formatNumber } = useI18n();
  const [confirming, setConfirming] = useState(false);

  const isDraft = announcement.sentAt === null;

  // Only asked for when the send button is armed: a list of twenty drafts should not fire twenty audience
  // queries to render.
  const audience = useQuery({
    queryKey: ['platform', 'announcements', announcement.id, 'audience'],
    queryFn: () => api.platform.previewAnnouncementAudience(announcement.id),
    enabled: confirming,
  });

  return (
    <Card
      title={announcement.title}
      action={isDraft
        ? <Badge tone="warning">{t('status.Draft')}</Badge>
        : <Badge tone="success">{t('announcements.status.sent')}</Badge>}
    >
      <Stack>
        <p style={{ whiteSpace: 'pre-wrap' }}>{announcement.body}</p>

        <p className="dg-muted">
          {t('announcements.audience.label')}: {describeAudience(announcement, t)}
        </p>

        {!isDraft && (
          <p className="dg-muted">
            {t('announcements.sentAt', {
              date: formatDateTime(announcement.sentAt!),
              people: formatNumber(announcement.recipientCount),
            })}
          </p>
        )}

        {isDraft && !confirming && (
          <Row>
            <Button disabled={busy} onClick={() => setConfirming(true)}>
              {t('announcements.send')}
            </Button>
            <Button variant="ghost" disabled={busy} onClick={onDiscard}>
              {t('announcements.discard')}
            </Button>
          </Row>
        )}

        {isDraft && confirming && (
          <Notice tone="warning" title={t('announcements.confirm.title')}>
            <Stack tight>
              {audience.isPending && <span>{t('announcements.confirm.counting')}</span>}
              {audience.error != null && <ErrorState error={audience.error} />}
              {audience.data && (
                <span>
                  {t('announcements.confirm.reach', {
                    tenants: formatNumber(audience.data.tenantsTargeted),
                    people: formatNumber(audience.data.recipientsNotified),
                  })}
                </span>
              )}
              <Row>
                <Button
                  disabled={busy || audience.isPending || audience.data?.recipientsNotified === 0}
                  onClick={() => { setConfirming(false); onSend(); }}
                >
                  {t('announcements.send.confirm')}
                </Button>
                <Button variant="secondary" onClick={() => setConfirming(false)}>
                  {t('action.cancel')}
                </Button>
              </Row>
            </Stack>
          </Notice>
        )}
      </Stack>
    </Card>
  );
}

function ComposeForm({ onCreated, onCancel }: { onCreated: () => void; onCancel: () => void }) {
  const api = useApi();
  const { t } = useI18n();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [scope, setScope] = useState<'all' | 'status'>('all');
  const [statuses, setStatuses] = useState<TenantStatus[]>([]);

  const create = useMutation({
    mutationFn: api.platform.createAnnouncement,
    onSuccess: onCreated,
  });

  function submit(event: FormEvent) {
    event.preventDefault();

    create.mutate({
      title,
      body,
      // An empty selection means everyone, which is what the server reads a null audience as.
      audience: scope === 'status' && statuses.length > 0
        ? { statuses, tenantIds: null }
        : null,
      scheduledAt: null,
    });
  }

  const toggle = (status: TenantStatus) =>
    setStatuses((current) =>
      current.includes(status) ? current.filter((s) => s !== status) : [...current, status]);

  return (
    <Card title={t('announcements.compose')} hint={t('announcements.compose.hint')}>
      <form onSubmit={submit}>
        <Stack>
          {create.error != null && <ErrorState error={create.error} />}

          <Field label={t('announcements.field.title')} required>
            {(props) => (
              <Input {...props} value={title} onChange={(event) => setTitle(event.target.value)} />
            )}
          </Field>

          <Field label={t('announcements.field.body')} required>
            {(props) => (
              <Textarea {...props} value={body} onChange={(event) => setBody(event.target.value)} />
            )}
          </Field>

          <Field label={t('announcements.audience.label')} hint={t('announcements.audience.hint')}>
            {(props) => (
              <Select
                {...props}
                value={scope}
                onChange={(event) => setScope(event.target.value as 'all' | 'status')}
              >
                <option value="all">{t('announcements.audience.all')}</option>
                <option value="status">{t('announcements.audience.byStatus')}</option>
              </Select>
            )}
          </Field>

          {scope === 'status' && (
            <Row>
              {TARGETABLE.map((status) => (
                <label key={status} className="dg-row" style={{ gap: 'var(--dg-space-2)' }}>
                  <input
                    type="checkbox"
                    checked={statuses.includes(status)}
                    onChange={() => toggle(status)}
                  />
                  <span>{t(`status.${status}`)}</span>
                </label>
              ))}
            </Row>
          )}

          <Row>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? t('action.saving') : t('announcements.saveDraft')}
            </Button>
            <Button type="button" variant="secondary" onClick={onCancel}>{t('action.cancel')}</Button>
          </Row>
        </Stack>
      </form>
    </Card>
  );
}

function describeAudience(
  announcement: PlatformAnnouncementResponse,
  t: (key: string, params?: Record<string, string | number>) => string,
): string {
  const audience = announcement.audience;
  if (!audience || (!audience.statuses?.length && !audience.tenantIds?.length)) {
    return t('announcements.audience.all');
  }

  const parts: string[] = [];
  if (audience.statuses?.length) parts.push(audience.statuses.map((s) => t(`status.${s}`)).join('، '));
  if (audience.tenantIds?.length) {
    parts.push(t('announcements.audience.someTenants', { count: audience.tenantIds.length }));
  }

  return parts.join(' · ');
}
